import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  CASHIER_API_01,
  COMMON_API_01,
  CASHIER_API_02,
  CASHIER_API_06,
  ORDER_API_03,
  EMPLOYEE_API_01,
  BUSINESSTYPECODE,
} from "../../api/ApiModel";
import { externalgetApi } from "../../services/external/externalApis";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme";
import DashboardIcon from "@mui/icons-material/Dashboard";

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SnackbarMessage from "../../components/common/SnackbarMessage";
import Navbar from "../../components/common/Navbar";
import DynamicLineChart from "../../components/charts/DynamicLineChart";
import DynamicBarChart from "../../components/charts/DynamicBarChart";
import {
  MonetizationOn as MonetizationOnIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  Receipt as ReceiptIcon,
  ArrowUpward as ArrowUpwardIcon,
} from "@mui/icons-material";
import {
  CON_STATUS_ACTIVE,
  CON_CAT_TYPE_RESTAURANT,
  CON_STATUS_PENDING,
  CON_STATUS_NO,
  CON_STATUS_RETURN,
  CON_IS_CASHIER_YES,
  CON_STATUS_RECEIVED,
  CON_STATUS_DELETED,
} from "../../utils/sys/Constants";
import DynamicSparklineChart from "../../components/charts/DynamicSparklineChart";

function Home() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const location = useLocation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [placedorderItems, setPlacedorderItems] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [barChartData, setBarChartData] = useState([]);

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE } = SYS_USER.USER_PARAM || {};

  useEffect(() => {
    // Check if there is a snackbar message passed from the Login page
    if (location.state?.snackbarMessage) {
      setSnackbarMessage(location.state.snackbarMessage);
      setSnackbarSeverity(location.state.snackbarSeverity);
      setSnackbarOpen(true); // Open the snackbar

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  useEffect(() => {
    fetchPlacedOrders();
    fetchOrderData();
    fetchDataUsers();
  }, []);

  const fetchOrderData = async (statusCode) => {
    setLoading(true);
    const params = {
      COMPCODE,
      LOCCODE,
      RESTCODE,
      FROMDATE: "1/1/2023",
      TODATE: new Date(),
      STS: CON_STATUS_ACTIVE,
    };
    try {
      const response = await externalgetApi(ORDER_API_03, "GET", params);
      setOrderData(response || []);

      console.log("fetchOrderData placed orders all list ", response);
    } catch (err) {
      console.error("Failed to fetch order data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total sales by summing the AMOUNT of each order
  const calculateTotalSales = () => {
    const totalSales =
      orderData?.reduce((total, order) => total + order.AMOUNT, 0) || 0;
    return formatPrice(totalSales); // Use formatPrice to format the total sales
  };

  function formatPrice(value) {
    if (typeof value === "number") {
      return value
        .toFixed(2) // Ensure two decimal places
        .replace(/\d(?=(\d{3})+\.)/g, "$&,"); // Add thousand separators
    }
    return value;
  }

  const fetchDataUsers = async () => {
    try {
      const result = await externalgetApi(EMPLOYEE_API_01, "get", {
        COMPCODE,
        LOCCODE,
        BUSINESSTYPECODE,
        STS: CON_STATUS_ACTIVE,
      });
      setData(result);
    } catch (err) {
      setError(err);
    }
  };
  const totalUsers = data?.length || 0;

  const fetchPlacedOrders = async () => {
    try {
      const response = await externalgetApi(CASHIER_API_01, "GET", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        STS: CON_STATUS_ACTIVE,
        STSN: CON_STATUS_NO,
        STSR: CON_STATUS_RECEIVED,
        ISCASHIER: CON_IS_CASHIER_YES,
      });

      setPlacedorderItems(response);

      // Calculate total orders
      const totalOrders = response.length;
      setTotalOrders(totalOrders);

      // Process data for BarChart by grouping by ORDERTYPE
      const orderTypeCounts = response.reduce((acc, order) => {
        const orderType = order.ORDERTYPE;
        acc[orderType] = (acc[orderType] || 0) + 1;
        return acc;
      }, {});

      // Prepare dataset for BarChart
      const barChartData = Object.keys(orderTypeCounts).map((orderType) => {
        const order = response.find((o) => o.ORDERTYPE === orderType); // Match the order
        return {
          x: orderType, // ORDERTYPE
          y: orderTypeCounts[orderType], // Total order count
          color: order?.COLORCODE || "#000000", // Use COLORCODE or default to black
        };
      });

      setBarChartData(barChartData);

      const endDate = new Date(); // Today's date
      endDate.setHours(23, 59, 59, 999); // Normalize to the end of the day

      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1); // Move back one month
      startDate.setDate(endDate.getDate()); // Align to the same day of the previous month
      startDate.setHours(0, 0, 0, 0); // Normalize to the start of the day

      // Process Data for LineChart (grouping orders by ORDERENDATE)
      const dailyOrderCounts = response.reduce((acc, order) => {
        const orderDate = new Date(order.ORDERENDATE); // Convert string to Date object
        if (orderDate >= startDate && orderDate <= endDate) {
          // Filter by date range
          const dateString = orderDate.toISOString().split("T")[0]; // Format the date (YYYY-MM-DD)
          acc[dateString] = (acc[dateString] || 0) + 1; // Group by formatted date
        }
        return acc;
      }, {});

      // Prepare the dataset for the LineChart
      const dataset = Object.keys(dailyOrderCounts).map((date) => ({
        x: new Date(date), // Convert date string to Date object
        y: dailyOrderCounts[date], // Order count for that date
      }));

      // Sort the dataset by date (ascending)
      dataset.sort((a, b) => a.x - b.x);

      setChartData(dataset);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.db_root}>
      <Navbar />
      {/* Header */}
      <Box sx={styles.db_header}>
        <Typography variant="h2" pb={1} sx={styles.db_headerTitle}>
          Dashboard
        </Typography>
        <Divider />
      </Box>

      {/* {loading ? "Loading..." : calculateTotalSales()} {totalOrders}{" "}
      {error ? "Error loading data" : totalUsers} */}
      {/* Main Content */}
      <Box sx={{ backgroundColor: "background.default" }}>
        <Grid container spacing={3}>
          {[
            {
              title: "Total Sales",
              value: `LKR ${calculateTotalSales().toLocaleString()}`,
              subText: "Total Sales for this month",
              icon: <MonetizationOnIcon fontSize="medium" />,
              color: "success.main",
            },
            {
              title: "Total Orders",
              value: `${totalOrders}`,
              subText: "New orders this week",
              icon: <BarChartIcon fontSize="medium" />,
              color: "primary.main",
            },
            {
              title: "Total Customers",
              value: `${totalUsers}`,
              subText: "Returning customers",
              icon: <PeopleIcon fontSize="medium" />,
              color: "error.main",
            },
          ].map((card, index) => (
            <Grid
              item
              xs={card.title === "Total Sales" ? 12 : 12}
              sm={card.title === "Total Sales" ? 12 : 12}
              md={card.title === "Total Sales" ? 12 : 6}
              lg={card.title === "Total Sales" ? 4 : 4}
              xl={card.title === "Total Sales" ? 4 : 4}
              key={index}
            >
              <Card
                sx={{
                  backgroundColor: "background.paper",
                  boxShadow: 2,
                  borderRadius: 1,
                  padding: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {/* Icon Container */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: card.color,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "12px",
                      color: "white",
                    }}
                  >
                    {card.icon}
                  </Box>
                  {/* Text Content */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontWeight: 500 }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: "text.primary", fontWeight: "bold" }}
                    >
                      {card.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {card.subText}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Charts - Start */}
      <Grid container mt={2} spacing={4}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Card sx={styles.db_card}>
            <Box sx={styles.db_header}>
              <Typography variant="body1" py={1} sx={styles.db_headerTitle}>
                Total Orders
              </Typography>
              <Divider />
            </Box>
            <CardContent>
              <DynamicBarChart dataset={barChartData} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Card sx={styles.db_card}>
            <Box sx={styles.db_header}>
              <Typography variant="body1" py={1} sx={styles.db_headerTitle}>
                Daily Orders
              </Typography>{" "}
              <Divider />
            </Box>
            <CardContent>
              <DynamicLineChart dataset={chartData} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Charts - End */}
      {/* Snackbar for success message from Login */}
      <SnackbarMessage
        open={snackbarOpen}
        handleClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default Home;
